"use client";
import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import { onSearchSubmit } from "../Navigation.helpers";
import { GlobalNavigationProps } from "..";
import { Search, XMarkThin } from "@/icons";
import { LocaleCode } from "@/types";

type ConsolidatedSearchBarProps = {
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  locale?: LocaleCode;
} & Pick<NonNullable<GlobalNavigationProps["superHeader"]>, "search">;

const ConsolidatedSearchBar: React.FC<ConsolidatedSearchBarProps> = ({
  search,
  isSearchOpen,
  setIsSearchOpen,
  locale,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const iconBtnStyles =
    "focus-visible:outline-swdc-blue-400 group cursor-pointer rounded outline-none hover:bg-swdc-gray-100 active:bg-swdc-gray-400 size-6 rounded-full focus-visible:ring-1 focus-visible:ring-[#003366]";

  return (
    <div
      className={cn(
        "relative min-h-6",
        isSearchOpen && "w-full max-w-[638px] text-left",
      )}
    >
      {isSearchOpen && (
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            onSearchSubmit(searchValue);
          }}
          className={cn(
            "relative mb-1 flex h-9 w-full justify-center rounded-full shadow-[0_1px_2px_0_rgba(0,0,0,0.30)]",
            {
              "ml-0.5 xl:ml-2": locale?.startsWith("es"),
              "xl:ml-2": locale?.startsWith("ja"),
              "ml-2": !locale?.startsWith("es") && !locale?.startsWith("ja"),
            },
          )}
          role="search"
        >
          <label className="sr-only" htmlFor="search-input">
            {search?.inputLabel}
          </label>
          <input
            className="border-swdc-silver-400 text-body-s bg-background-highlight text-body-primary placeholder:text-body-primary w-full rounded-full border py-1.5 pr-18 pl-4 focus:border-black focus:outline focus:outline-transparent"
            id="search-input"
            type="text"
            placeholder={search?.inputPlaceholder}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            ref={searchInputRef}
            {...getDataAutomationGroup("input-field")}
          />
          <div className="absolute inset-y-0 right-2.25 flex flex-row items-center justify-center gap-2.75">
            <button
              className={cn(iconBtnStyles)}
              type="submit"
              {...getDataLinkAttributes({
                dld: search?.submitButtonDLD,
                dlt: search?.submitButtonDLT,
              })}
              {...getDataAutomationId("search-icon")}
            >
              <span className="sr-only">{search?.submitButtonLabel}</span>
              <Search
                type="icon-search-bar"
                width={20}
                height={20}
                className="text-swdc-black-600 size-5 group-active:[&_path]:fill-white"
              />
            </button>
            <button
              className={cn(iconBtnStyles, "-mt-px")}
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
              }}
              {...getDataLinkAttributes({
                dld: search?.hideButtonDLD,
                dlt: search?.hideButtonDLT,
              })}
              {...getDataAutomationId("close-icon")}
            >
              <span className="sr-only">{search?.hideButtonLabel}</span>
              <XMarkThin className="text-swdc-black-600 size-6 group-active:[&_path]:fill-white" />
            </button>
          </div>
        </form>
      )}
      {!isSearchOpen && (
        <button
          className="group focus-visible:ring-swdc-focus-primary after:bg-swdc-orange-400 after:rounded-corner-pill relative block rounded-sm px-0.5 pt-0.5 pb-1.5 text-center outline-none after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0 after:content-[''] hover:after:h-0.5 focus-visible:ring-1 focus-visible:ring-offset-1 active:after:h-0.5"
          onClick={() => {
            setIsSearchOpen(true);
          }}
          {...getDataLinkAttributes({
            dld: search?.showButtonDLD,
            dlt: search?.showButtonDLT,
          })}
          {...getDataAutomationId("search-icon")}
        >
          <span className="sr-only">{search?.showButtonLabel}</span>
          <Search
            type="icon-search-bar"
            width={20}
            height={20}
            className="text-swdc-black-600 size-5"
          />
        </button>
      )}
    </div>
  );
};

export default ConsolidatedSearchBar;
